import React, { createContext, useContext, useState, useEffect } from 'react';

// Import all translation files
import translationsEN from './translations/translations-en.json';
import translationsRU from './translations/translations-ru.json';
import translationsUZ from './translations/translations-uz.json';
import translationsFR from './translations/translations-fr.json';

// Available languages with native names
export const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', default: true },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', default: true },
  { code: 'uz', name: 'Uzbek', nativeName: 'O\'zbekcha', flag: '🇺🇿', default: true },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', default: true }
];

// Translations object
const translations = {
  en: translationsEN,
  ru: translationsRU,
  uz: translationsUZ,
  fr: translationsFR
};

// Language Context
const LanguageContext = createContext();

// Language Provider Component
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Try to load saved language from localStorage
    const savedLang = localStorage.getItem('dashboardLanguage');
    return savedLang && translations[savedLang] ? savedLang : 'en';
  });

  const [enabledLanguages, setEnabledLanguages] = useState(() => {
    // Load enabled languages from localStorage, default to all default languages
    const saved = localStorage.getItem('dashboardEnabledLanguages');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return LANGUAGES.filter(l => l.default).map(l => l.code);
      }
    }
    return LANGUAGES.filter(l => l.default).map(l => l.code);
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('dashboardLanguage', currentLanguage);
  }, [currentLanguage]);

  useEffect(() => {
    // Save enabled languages to localStorage
    localStorage.setItem('dashboardEnabledLanguages', JSON.stringify(enabledLanguages));
  }, [enabledLanguages]);

  const changeLanguage = (langCode) => {
    if (translations[langCode] && enabledLanguages.includes(langCode)) {
      setCurrentLanguage(langCode);
    }
  };

  const toggleLanguage = (langCode) => {
    setEnabledLanguages(prev => {
      // Always keep at least one language enabled
      if (prev.length === 1 && prev.includes(langCode)) {
        return prev;
      }
      
      if (prev.includes(langCode)) {
        // Disabling - if current language is being disabled, switch to first enabled
        if (currentLanguage === langCode) {
          const remaining = prev.filter(l => l !== langCode);
          setCurrentLanguage(remaining[0]);
        }
        return prev.filter(l => l !== langCode);
      } else {
        return [...prev, langCode];
      }
    });
  };

  const resetLanguages = () => {
    const defaults = LANGUAGES.filter(l => l.default).map(l => l.code);
    setEnabledLanguages(defaults);
    if (!defaults.includes(currentLanguage)) {
      setCurrentLanguage(defaults[0]);
    }
  };

  // Get nested translation value
  const t = (key, replacements = {}) => {
    const keys = key.split('.');
    let value = translations[currentLanguage];

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        // Fallback to English if translation not found
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object') {
            value = value[fallbackKey];
          } else {
            return key; // Return key if not found in English either
          }
        }
        break;
      }
    }

    // Handle replacements (e.g., {count} in string)
    if (typeof value === 'string' && Object.keys(replacements).length > 0) {
      Object.keys(replacements).forEach(replKey => {
        value = value.replace(`{${replKey}}`, replacements[replKey]);
      });
    }

    return value || key;
  };

  // Get array from translations (e.g., month names)
  const tArray = (key) => {
    const result = t(key);
    return Array.isArray(result) ? result : [];
  };

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    tArray,
    languages: LANGUAGES,
    enabledLanguages,
    toggleLanguage,
    resetLanguages,
    availableLanguages: LANGUAGES.filter(l => enabledLanguages.includes(l.code)),
    currentLangObj: LANGUAGES.find(l => l.code === currentLanguage)
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use translation
export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

// Language Selector Component
export const LanguageSelector = () => {
  const { currentLanguage, changeLanguage, availableLanguages, currentLangObj } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 16px',
          background: 'rgba(255,255,255,0.15)',
          border: '1px solid rgba(255,255,255,0.25)',
          borderRadius: 6,
          color: 'white',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 500,
          transition: 'all 0.2s',
          outline: 'none'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
      >
        <span style={{ fontSize: 18 }}>{currentLangObj?.flag}</span>
        <span>{currentLangObj?.nativeName}</span>
        <span style={{ fontSize: 10, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          ▼
        </span>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setIsOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
          />

          {/* Dropdown Menu */}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 8,
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              minWidth: 180,
              zIndex: 1000,
              overflow: 'hidden'
            }}
          >
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  changeLanguage(lang.code);
                  setIsOpen(false);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 16px',
                  background: currentLanguage === lang.code ? '#f0f7ff' : 'white',
                  border: 'none',
                  borderBottom: '1px solid #f0f0f0',
                  color: currentLanguage === lang.code ? '#0078d4' : '#333',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: currentLanguage === lang.code ? 600 : 400,
                  textAlign: 'left',
                  transition: 'all 0.15s',
                  outline: 'none'
                }}
                onMouseEnter={(e) => {
                  if (currentLanguage !== lang.code) {
                    e.currentTarget.style.background = '#f8f8f8';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentLanguage !== lang.code) {
                    e.currentTarget.style.background = 'white';
                  }
                }}
              >
                <span style={{ fontSize: 20 }}>{lang.flag}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{lang.nativeName}</div>
                  <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{lang.name}</div>
                </div>
                {currentLanguage === lang.code && (
                  <span style={{ color: '#0078d4', fontSize: 16 }}>✓</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Language Settings Component
export const LanguageSettings = ({ onClose }) => {
  const { languages, enabledLanguages, toggleLanguage, resetLanguages, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => onClose?.(), 200);
  };

  useEffect(() => {
    setIsOpen(true);
  }, []);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 9998,
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 0.2s'
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) scale(${isOpen ? 1 : 0.95})`,
          background: 'white',
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          maxWidth: 500,
          width: '90%',
          maxHeight: '80vh',
          overflow: 'hidden',
          zIndex: 9999,
          opacity: isOpen ? 1 : 0,
          transition: 'all 0.2s'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#333' }}>
              {t('languageSettings.title')}
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#666' }}>
              {t('languageSettings.description')}
            </p>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 24,
              color: '#999',
              cursor: 'pointer',
              padding: 4,
              lineHeight: 1,
              outline: 'none'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 24, maxHeight: 'calc(80vh - 140px)', overflowY: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {languages.map((lang) => (
              <label
                key={lang.code}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 12,
                  background: enabledLanguages.includes(lang.code) ? '#f0f7ff' : '#f8f8f8',
                  border: `2px solid ${enabledLanguages.includes(lang.code) ? '#0078d4' : '#e0e0e0'}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <input
                  type="checkbox"
                  checked={enabledLanguages.includes(lang.code)}
                  onChange={() => toggleLanguage(lang.code)}
                  disabled={enabledLanguages.length === 1 && enabledLanguages.includes(lang.code)}
                  style={{
                    width: 18,
                    height: 18,
                    cursor: 'pointer'
                  }}
                />
                <span style={{ fontSize: 24 }}>{lang.flag}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>
                    {lang.nativeName}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {lang.name}
                    {lang.default && (
                      <span style={{ marginLeft: 8, color: '#0078d4', fontWeight: 500 }}>
                        • {t('languageSettings.default')}
                      </span>
                    )}
                  </div>
                </div>
                {enabledLanguages.length === 1 && enabledLanguages.includes(lang.code) && (
                  <div style={{ fontSize: 11, color: '#999', fontStyle: 'italic' }}>
                    {t('languageSettings.required')}
                  </div>
                )}
              </label>
            ))}
          </div>

          <div
            style={{
              marginTop: 20,
              padding: 12,
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: 6,
              fontSize: 12,
              color: '#856404'
            }}
          >
            <strong>Note:</strong> {t('languageSettings.note')}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            gap: 12,
            justifyContent: 'flex-end'
          }}
        >
          <button
            onClick={resetLanguages}
            style={{
              padding: '8px 16px',
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: 6,
              color: '#666',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              transition: 'all 0.2s',
              outline: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f8f8f8'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
          >
            {t('languageSettings.resetToDefaults')}
          </button>
          <button
            onClick={handleClose}
            style={{
              padding: '8px 20px',
              background: '#0078d4',
              border: 'none',
              borderRadius: 6,
              color: 'white',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              transition: 'all 0.2s',
              outline: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#005a9e'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#0078d4'}
          >
            {t('languageSettings.done')}
          </button>
        </div>
      </div>
    </>
  );
};
